import dayjs from 'dayjs';
import z from 'zod';

export const draftOrderLineBaseSchema = z.object({
  qty: z.coerce.number().min(1, { error: 'Quantity is required' }),
  unit_price: z.string().nonempty({ error: 'Unit price is required' }),
  needed_by: z
    .string()
    .nonempty({ error: 'Needed by date is required' })
    .refine((date) => dayjs(date).isValid(), {
      error: 'Select a valid date.',
    }),
  notes: z.string().optional(),
  // Release is optional: POs are project-scoped and may be project-level with
  // no release. When set it must be a valid release id (positive).
  release: z.coerce.number().int().positive().optional(),
});
const draftOrderLineSystemPartSchema = z.object({
  ...draftOrderLineBaseSchema.shape,
  part: z.coerce.number({ error: 'Part is required' }),
  manual_part_number: z.never().optional(),
});
const draftOrderLineManualPartSchema = z.object({
  ...draftOrderLineBaseSchema.shape,
  manual_part_number: z
    .string()
    .nonempty({ error: 'Manual part number is required' }),
  part: z.number().optional(),
});

export const post = {
  body: z
    .union([draftOrderLineSystemPartSchema, draftOrderLineManualPartSchema])
    .array(),
  response: z.object({
    id: z.number(),
    created_at: z.date(),
    updated_at: z.date(),
    qty: z.number(),
    unit_price: z.string(),
    needed_by: z.date(),
    notes: z.string().optional(),
    draft: z.number(),
    part: z.number(),
  }),
  params: z.object({
    draft_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postSchema = z.object(post);
export type Post = z.infer<typeof postSchema>;

const get = {
  body: z.never(),
  response: z.object({
    id: z.number(),
    created_at: z.date(),
    updated_at: z.date(),
    qty: z.number(),
    unit_price: z.string(),
    needed_by: z.date(),
    notes: z.string().optional(),
    draft: z.number(),
    part: z.number(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const getSchema = z.object(get);
export type Get = z.infer<typeof getSchema>;

const put = {
  body: z.object({
    qty: z.coerce.number(),
    unit_price: z.string(),
    needed_by: z.string().refine((date) => dayjs(date).isValid(), {
      error: 'Select a valid date.',
    }),
    notes: z.string().optional(),
    draft: z.coerce.number(),
    part: z.coerce.number(),
  }),
  response: z.object({
    id: z.number(),
    created_at: z.date(),
    updated_at: z.date(),
    qty: z.number(),
    unit_price: z.string(),
    needed_by: z.date(),
    notes: z.string().optional(),
    draft: z.number(),
    part: z.number(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const putSchema = z.object(put);
export type Put = z.infer<typeof putSchema>;

export const patch = {
  body: z
    .object({
      qty: z.coerce.number(),
      unit_price: z.string(),
      needed_by: z.string().refine((date) => dayjs(date).isValid(), {
        error: 'Select a valid date.',
      }),
      notes: z.string().optional(),
      draft: z.coerce.number(),
      part: z.coerce.number(),
    })
    .partial(),
  response: z.object({
    qty: z.number(),
    unit_price: z.string(),
    needed_by: z.date(),
    notes: z.string().optional(),
    draft: z.number(),
    part: z.number(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchSchema = z.object(patch);
export type Patch = z.infer<typeof patchSchema>;

const remove = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const deleteSchema = z.object(remove);
export type Delete = z.infer<typeof deleteSchema>;
